/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import {
    SessionCreateDeviceTypeEnum,
    SessionCreateDrmTypeEnum,
    SessionCreateTransportEncodingTypeEnum,
    SessionErrorResponseCodeEnum,
} from 'client-code/session-manager';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { TSessionCreateModel } from '../interfaces/TSessionCreateModel';
import { IServices } from '../../interfaces/IServices';
import { ServicesClientImpl } from '../../impls/ServicesClientImpl';
import { Session } from '../../entities/Session';
import { ErrorTypes } from '../../error';
import { TCreateSessionParams } from '../../types/TCreateSessionParams';
import { DeviceModel } from '../../device';
import { TSession } from '../../types';
import { RetryMechanism } from '../../retrymechanism/RetryMechanism';
import { UniqueIdentifier } from '../../utils/UniqueIdentifier';
import { RetryOptions } from '../../retrymechanism/RetryOptions';
import { Log, QoEAppLogger, VuLogger } from '../../logging';
import { TInHomeLocation } from '../../types/TInHomeLocation';
import { Storage } from '../../storage/Storage';
import { Constants } from '../../constants';
import { SESSION_MANAGER_GRACE_PERIOD } from '../../../config';
import { Channel } from '../../entities/Channel';
import { StreamingSessionImpl } from './StreamingSessionImpl';

export class IpLinearStreamingSessionImpl extends StreamingSessionImpl {
    TAG: string = 'IpLinearStreamingSession';

    private static oIStreamingSession: IpLinearStreamingSessionImpl;

    private sessionManagerGracePeriodTimeoutId: any;

    static getInstance(): IpLinearStreamingSessionImpl {
        if (!IpLinearStreamingSessionImpl.oIStreamingSession)
            IpLinearStreamingSessionImpl.oIStreamingSession = new IpLinearStreamingSessionImpl();
        return IpLinearStreamingSessionImpl.oIStreamingSession;
    }

    createStreamingSession(sessionCreateModel: TSessionCreateModel): void {
        const { channel, transportEncodingType, playUri } = sessionCreateModel;
        const { partnerStationId, linearProviderPartnerId } = channel;
        if (!linearProviderPartnerId) {
            this.handlePlaybackError(ErrorTypes.LIVE_TV_DEVICE_ERROR_MISSING_CONFIG_V904);
            return;
        }
        // Need to update devicetype, drmtype, transportEncodingType based on actual data
        const sessionParams: TCreateSessionParams = {
            deviceType: SessionCreateDeviceTypeEnum.IptvSmartTv,
            drmType: SessionCreateDrmTypeEnum.Widevine,
            partnerId: linearProviderPartnerId || '',
            partnerServicesCustomerId: DeviceModel.getTiVoSettings().getPartnerCustomerId(),
            partnerStationId: partnerStationId || '',
            transportEncodingType: <SessionCreateTransportEncodingTypeEnum>transportEncodingType,
        };
        clearTimeout(this.timeoutId);
        clearInterval(this.timeoutId);
        const createSession = this.services.createSession.bind(this.services);
        RetryMechanism.callServiceWithRetry<TSession>(
            createSession,
            [sessionParams, UniqueIdentifier.getNextSessionNetworkCallId()],
            <RetryOptions>this.SESSION_MANAGER_DEFAULT_RETRY_OPTIONS,
        )
            .then((response: TSession) => {
                if (response.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                    this.oSession = response.session;
                    this.onSessionCreated.emit(this.oSession);
                    this.updateTuningUriInChannelData(this.oSession, channel, playUri || '');
                    clearTimeout(this.sessionManagerGracePeriodTimeoutId);
                    const { firstKeepAliveSeconds, keepAliveIntervalSeconds, sessionId } =
                        response.session;
                    VuLogger.setSessionId(sessionId);
                    this.timeoutId = setTimeout(() => {
                        // Time in second converting to millisecond
                        this.setKeepAliveInterval(keepAliveIntervalSeconds * 1000);
                        // Time in second converting to millisecond
                    }, firstKeepAliveSeconds && firstKeepAliveSeconds * 1000);
                    // logging VuLogger.logStreamingRecovery if sessionCreate Recovers
                    if (VuLogger.sessionErrorRecoveryFlag) {
                        VuLogger.logStreamingRecovery(VuLogger.StreamingRecoveryType.SESSION_ERROR);
                        VuLogger.sessionErrorRecoveryFlag = false;
                    }
                }
            })
            .catch((error: any) => {
                // service is unreachable
                if (error.sessionNetworkCallId === UniqueIdentifier.getSessionNetworkCallId()) {
                    const inHomeLocationData: TInHomeLocation = Storage.getInstance().getItem(
                        Storage.Keys.IN_HOME_LOCATION_DATA,
                    );
                    // setting VuLogger.sessionErrorRecoveryFlag to true as it went into recovery
                    VuLogger.sessionErrorRecoveryFlag = true;
                    if (
                        (!error.response ||
                            error.response.data.code ===
                                SessionErrorResponseCodeEnum.UnknownLocation) &&
                        inHomeLocationData.status !== Constants.IN_HOME_LOCATION_STATUS.UNREACHABLE
                    ) {
                        clearTimeout(this.sessionManagerGracePeriodTimeoutId);
                        QoEAppLogger.logStreamingStartEvent();
                        QoEAppLogger.logVideoPlayerOpenStreamEvent();
                        this.sessionManagerGracePeriodTimeoutId = setTimeout(() => {
                            VuLogger.sessionErrorRecoveryFlag = false;
                            this.handlePlaybackError(
                                ErrorTypes.LIVE_TV_GRACE_PERIOD_ENDED_ERROR_C718,
                            );
                        }, SESSION_MANAGER_GRACE_PERIOD);
                    } else {
                        VuLogger.sessionErrorRecoveryFlag = false;
                        this.handleSessionError(error);
                        clearTimeout(this.sessionManagerGracePeriodTimeoutId);
                        Diagnostics.error(this.TAG, `{createStreamingSession: ${error}}`);
                        Log.e(this.TAG, `{createStreamingSession: ${error}}`);
                    }
                }
            });
    }

    private updateTuningUriInChannelData = (data: Session, channel: Channel, playUri: string) => {
        if (data && data.drmType && data.transportEncodingType && data.playlistUrl) {
            const playlistUrl = data.playlistUrl.replace(/^http?:\/\//, 'https://');
            if (playUri !== playlistUrl) {
                const drmServerTypeString = data.drmType!;
                const transportEncodingType = data.transportEncodingType!;
                const partnerId = data.partnerId.split('.')[1];
                const urlToUpdate = `ip-abr://${transportEncodingType}/${partnerId}/${drmServerTypeString}/any/${data.playlistUrl}`;
                const services: IServices = new ServicesClientImpl();
                services.updateChannelUrl(channel.channelNumber!, urlToUpdate);
                Diagnostics.info(this.TAG, 'updateTuningUriInChannelData');
                Log.i(this.TAG, 'updateTuningUriInChannelData');
            }
        }
    };
}
